import React, { useState } from 'react';
import { formatDateByTimezone, formatTime, getTimezoneAbbreviation } from '@/lib/utils';

import { EventInfoCard } from '@/components/ui/EventInfoCard';
import UpdateCartItemModal from './UpdateCartItemModal';
import AddOnDetailModal from './AddOnDetailModal';
import EditAddOnGuestsModal from './EditAddOnGuestsModal';
import EditGuestsModal from './EditGuestsModal';
import DeleteCartItemModal from './DeleteCartItemModal';
import CartItemErrorState from './CartItemErrorState';
import CartItemHeader from './CartItemHeader';
import CartItemAddOnsDisplay from './CartItemAddOnsDisplay';
import CartItemEditMode from './CartItemEditMode';
import CartItemStatusWarnings from './CartItemStatusWarnings';
import CartItemPriceSummary from './CartItemPriceSummary';
import CartItemEditActions from './CartItemEditActions';
import type { EventBookingCategoryResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import type { CartItemResponse, AddOnResponse } from '@/features/cart/types';
import { useCartItemStatus } from '@/features/cart/hooks/useCartItemStatus';
import { useCartItemEdit } from '@/features/cart/hooks/useCartItemEdit';
import { useCartItemOperations } from '@/features/cart/hooks/useCartItemOperations';
import { getEventImage, getAddOnById, getGuestsWithAddOn } from '@/features/cart/utils/cartItemUtils';
import { TierRibbon } from '@/features/booking/components/TierRibbon';
import type { BookingCategoryClass } from '@/features/booking/types/bookingTypes';

interface CartItemCardProps {
  cartItem: CartItemResponse;
  addOns: AddOnResponse[];
  eventBookingCategories: EventBookingCategoryResponse[];
  onUpdate: () => void;
  onRemove: () => void;
  itemStatus?: import('@/features/cart/types').CartItemStatus | null;
  cartStatus?: import('@/features/cart/types').CartStatus;
  clubHandle?: string | null;
  isGuestCart?: boolean;
  onStatusUpdate?: (itemId: string, status: import('@/features/cart/types').CartItemStatus) => void;
}

const CartItemCard: React.FC<CartItemCardProps> = ({ 
  cartItem, 
  addOns, 
  eventBookingCategories, 
  onUpdate, 
  onRemove,
  itemStatus,
  cartStatus,
  clubHandle,
  isGuestCart,
  onStatusUpdate
}) => {
  // Status management
  const { statusLoading, statusCode, addOnStatuses, borderColor, canEdit } = useCartItemStatus(itemStatus, cartStatus, cartItem.id);

  // Edit mode management
  const {
    isEditing,
    editGuestCount,
    editAddOnsPerGuest,
    removedItems,
    removedGuests,
    startEdit,
    cancelEdit,
    removeGuest,
    removeAddOnFromGuest,
    restoreGuest,
    restoreAddOnFromGuest,
    isItemMarkedForRemoval,
    getEditModeGuestsWithAddOn,
    resetEditState,
    removeAddOnCompletely,
    restoreAddOnCompletely,
    isAddOnCompletelyRemoved,
    setAddOnQuantityForGuest,
  } = useCartItemEdit(cartItem);

  // Operations
  const { isUpdating, isRemoving, updateCartItem, confirmPrice, removeItem } = useCartItemOperations();

  // Modal states
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedAddOnForModal, setSelectedAddOnForModal] = useState<{ addOn: AddOnResponse; totalPrice: number; guests: number[]; pricePerGuest: number; quantitiesPerGuest: number[] } | null>(null);
  const [selectedAddOnForEdit, setSelectedAddOnForEdit] = useState<{ addOnId: string; guests: number[] } | null>(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Find the matching event booking category from the provided array
  const eventBookingCategory = cartItem.event_booking_category?.id
    ? eventBookingCategories.find(ebc => ebc.id === cartItem.event_booking_category?.id)
    : null;
  
  const eventSchedule = eventBookingCategory?.event_schedule;
  const event = eventSchedule?.event;
  const bookingCategory = eventBookingCategory?.booking_category;
  
  // Get timezone from metadata hook
  const businessTimeZone = cartItem.timezone_id || 'Europe/London';

  const eventImage = getEventImage(event || null, bookingCategory || null);

  if (!eventBookingCategory || !event) {
    return <CartItemErrorState />;
  }

  const eventDate = eventSchedule?.start_time 
    ? `${formatDateByTimezone(eventSchedule.start_time, businessTimeZone, { format: 'date', includeTimezone: false })} ${getTimezoneAbbreviation(eventSchedule.start_time, businessTimeZone)}`
    : 'Date TBD';
  const eventTime = eventSchedule?.start_time 
    ? formatTime(eventSchedule.start_time, businessTimeZone)
    : 'Time TBD';

  // Handlers
  const handleUpdate = async () => {
    await updateCartItem(
      cartItem,
      editGuestCount,
      editAddOnsPerGuest,
      removedGuests,
      removedItems,
      () => {
        cancelEdit();
        setShowUpdateModal(false);
        resetEditState();
        onUpdate();
      }
    );
  };

  const handleConfirmPrice = async () => {
    await confirmPrice(
      cartItem.id,
      onUpdate,
      onStatusUpdate ? (status) => onStatusUpdate(cartItem.id, status) : undefined
    );
  };

  const handleRemoveItem = async () => {
    await removeItem(cartItem.id, onRemove);
  };

  const handleAddOnCardClick = (addOnId: string) => {
    const addOnData = getAddOnById(addOns, addOnId);
    if (!addOnData) return;

    const guests = getGuestsWithAddOn(cartItem, addOnId, false);
    if (guests.length === 0) return;

    // Calculate prices and quantities per guest
    const prices: number[] = [];
    const quantities: number[] = [];
    cartItem.add_ons_per_guest.forEach((guestAddOns) => {
      if (!guestAddOns) return;
      const selectedAddOn = guestAddOns.find(ao => ao.add_on_id === addOnId);
      if (selectedAddOn) {
        const qty = selectedAddOn.quantity || 1;
        quantities.push(qty);
        prices.push(selectedAddOn.price * qty);
      }
    });

    // pricePerGuest is the unit price (without quantity multiplication)
    const unitPrice = cartItem.add_ons_per_guest.flatMap(g => g || []).find(ao => ao.add_on_id === addOnId)?.price || 0;
    const totalPrice = prices.reduce((sum, p) => sum + p, 0);

    setSelectedAddOnForModal({
      addOn: addOnData,
      totalPrice,
      guests,
      pricePerGuest: unitPrice,
      quantitiesPerGuest: quantities
    });
  };

  const handleAddOnEditClick = (addOnId: string, guests: number[]) => {
    setSelectedAddOnForEdit({ addOnId, guests });
  };

  return (
    <EventInfoCard
      eventImageUrl={eventImage}
      borderColorClass={borderColor}
      animate={true}
    >
      <div className="relative">
        {/* Header Section */}
        <CartItemHeader
          event={event}
          eventSchedule={eventSchedule}
          bookingCategory={bookingCategory || null}
          eventBookingCategorySlotDuration={eventBookingCategory?.custom_slot_duration}
          cartItem={cartItem}
          businessTimeZone={businessTimeZone}
          eventDate={eventDate}
          eventTime={eventTime}
          canEdit={canEdit}
          isEditing={isEditing}
          isGuestCart={isGuestCart}
          editGuestCount={editGuestCount}
          onStartEdit={startEdit}
          onDeleteClick={() => setShowDeleteModal(true)}
          onOpenGuestModal={isEditing ? () => setShowGuestModal(true) : undefined}
        />

        {/* Tier Ribbon Separator */}
        <TierRibbon
          tierClass={(bookingCategory?.class as BookingCategoryClass) || 'Standard'}
          variant="separator"
          textSuffix="TIER"
        />

        {/* Content Section */}
        <div className="p-6 bg-black/40">

          {/* Add-ons Display */}
          {!isEditing && (
            <CartItemAddOnsDisplay
              cartItem={cartItem}
              addOns={addOns}
              addOnStatuses={addOnStatuses}
              onAddOnClick={handleAddOnCardClick}
              clubHandle={clubHandle}
              currency={cartItem.currency}
            />
          )}

          {/* Edit Mode - Guest Management */}
          {isEditing && (
            <CartItemEditMode
              cartItem={cartItem}
              editAddOnsPerGuest={editAddOnsPerGuest}
              addOns={addOns}
              removedGuests={removedGuests}
              removedItems={removedItems}
              onAddOnClick={handleAddOnEditClick}
              getEditModeGuestsWithAddOn={getEditModeGuestsWithAddOn}
              isItemMarkedForRemoval={isItemMarkedForRemoval}
              removeAddOnCompletely={removeAddOnCompletely}
              restoreAddOnCompletely={restoreAddOnCompletely}
              isAddOnCompletelyRemoved={isAddOnCompletelyRemoved}
            />
          )}

          {/* Status Warnings */}
          <CartItemStatusWarnings
            statusCode={statusCode}
            statusLoading={statusLoading}
            isUpdating={isUpdating}
            isRemoving={isRemoving}
            onConfirmPrice={handleConfirmPrice}
            onRemoveItem={handleRemoveItem}
            onStartEdit={startEdit}
            isTableBooking={!!bookingCategory?.is_table_booking}
          />

          {/* Price Summary */}
          <CartItemPriceSummary
            cartItem={cartItem}
            isEditing={isEditing}
            editGuestCount={editGuestCount}
            editAddOnsPerGuest={editAddOnsPerGuest}
            removedGuests={removedGuests}
            removedItems={removedItems}
            clubHandle={clubHandle}
            isTableBooking={bookingCategory?.is_table_booking}
            tableSize={cartItem.table_size}
          />

          {/* Edit Mode Actions */}
          {isEditing && (
            <CartItemEditActions
              onCancel={cancelEdit}
              onUpdate={() => setShowUpdateModal(true)}
            />
          )}
        </div>
      </div>

      {/* Update Confirmation Modal */}
      <UpdateCartItemModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onConfirm={handleUpdate}
        isUpdating={isUpdating}
      />

      {/* Add-on Detail Modal */}
      {selectedAddOnForModal && (
        <AddOnDetailModal
          isOpen={!!selectedAddOnForModal}
          onClose={() => setSelectedAddOnForModal(null)}
          addOn={selectedAddOnForModal.addOn}
          totalPrice={selectedAddOnForModal.totalPrice}
          guestsWithAddOn={selectedAddOnForModal.guests}
          pricePerGuest={selectedAddOnForModal.pricePerGuest}
          description={selectedAddOnForModal.addOn.description}
          quantitiesPerGuest={selectedAddOnForModal.quantitiesPerGuest}
          eventStartTime={eventSchedule?.start_time}
          earlyEntryDuration={eventSchedule?.early_entry_duration}
          currency={cartItem.currency}
        />
      )}

      {/* Edit Add-on Guests Modal */}
      {selectedAddOnForEdit && (() => {
        const addOnData = getAddOnById(addOns, selectedAddOnForEdit.addOnId);
        if (!addOnData) return null;
        
        const currentGuestsWithAddOn = getEditModeGuestsWithAddOn(selectedAddOnForEdit.addOnId);
        
        const pricePerGuest = editAddOnsPerGuest
          .filter(guestAddOns => guestAddOns != null)
          .find(guestAddOns => 
            guestAddOns.some(ao => ao.add_on_id === selectedAddOnForEdit.addOnId)
          )?.find(ao => ao.add_on_id === selectedAddOnForEdit.addOnId)?.price || 0;
        
        const totalPriceForModal = currentGuestsWithAddOn.length * pricePerGuest;
        
        const getRemovedGuestsForThisAddOn = () => {
          const removed: number[] = [];
          editAddOnsPerGuest.forEach((guestAddOns, index) => {
            if (guestAddOns && 
                guestAddOns.some(ao => ao.add_on_id === selectedAddOnForEdit.addOnId) && 
                isItemMarkedForRemoval(index, selectedAddOnForEdit.addOnId) &&
                !removedGuests.has(index)) {
              removed.push(index);
            }
          });
          return removed;
        };
        
        return (
          <EditAddOnGuestsModal
            isOpen={!!selectedAddOnForEdit}
            onClose={() => setSelectedAddOnForEdit(null)}
            addOn={addOnData}
            guestsWithAddOn={currentGuestsWithAddOn}
            allGuests={cartItem.total_guests}
            pricePerGuest={pricePerGuest}
            totalPrice={totalPriceForModal}
            removedGuests={removedGuests}
            removedAddOns={new Set(getRemovedGuestsForThisAddOn())}
            editAddOnsPerGuest={editAddOnsPerGuest}
            originalAddOnsPerGuest={cartItem.add_ons_per_guest}
            onRemoveGuest={(guestIndex) => {
              removeAddOnFromGuest(guestIndex, selectedAddOnForEdit.addOnId);
            }}
            onRestoreGuest={(guestIndex) => {
              restoreGuest(guestIndex);
            }}
            onRestoreAddOn={(guestIndex) => {
              restoreAddOnFromGuest(guestIndex, selectedAddOnForEdit.addOnId);
            }}
            onQuantityChange={(guestIndex, addOnId, newQuantity) => {
              setAddOnQuantityForGuest(guestIndex, addOnId, newQuantity);
            }}
          />
        );
      })()}

      {/* Edit Guests Modal */}
      <EditGuestsModal
        isOpen={showGuestModal}
        onClose={() => setShowGuestModal(false)}
        allGuests={cartItem.total_guests}
        editAddOnsPerGuest={editAddOnsPerGuest}
        addOns={addOns}
        removedGuests={removedGuests}
        removedItems={removedItems}
        onRemoveGuest={removeGuest}
        onRestoreGuest={restoreGuest}
        getAddOnById={(addOnId: string) => getAddOnById(addOns, addOnId)}
      />

      {/* Delete Cart Item Confirmation Modal */}
      <DeleteCartItemModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await handleRemoveItem();
          setShowDeleteModal(false);
        }}
        eventName={event?.name}
        isDeleting={isRemoving}
      />
    </EventInfoCard>
  );
};

export default CartItemCard;
